import type { MarkType, Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import type { Editor } from '@tiptap/react';

import {
    normalizeHexColor,
    setStyleColor,
    uniformStyleColor,
    type StyleColorProperty,
} from './documentColors';

interface TableCellRef {
    node: ProseMirrorNode;
    pos: number;
}

export function selectedTableCells(state: EditorState): TableCellRef[] {
    const { selection } = state;
    if (selection instanceof CellSelection) {
        const cells: TableCellRef[] = [];
        selection.forEachCell((node, pos) => {
            if (node) cells.push({ node, pos });
        });
        return cells;
    }

    const { $from } = selection;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
            return [{ node, pos: $from.before(depth) }];
        }
    }
    return [];
}

function cellStyle(node: ProseMirrorNode): string | null {
    return typeof node.attrs.style === 'string' ? node.attrs.style : null;
}

export function activeTableStyleColor(
    state: EditorState,
    property: StyleColorProperty
): string | null {
    const cells = selectedTableCells(state);
    const result = uniformStyleColor(
        cells.map((cell) => cellStyle(cell.node)),
        property
    );
    return result.uniform ? result.color : null;
}

/**
 * Header CSS paints th text white unless the cell carries its own color.
 * Marks keep the same color on the text itself, which is what preview and
 * PDF keep when they do not inherit from the cell.
 */
function paintCellTextColor(
    tr: Transaction,
    doc: ProseMirrorNode,
    cellPos: number,
    cellNode: ProseMirrorNode,
    color: string | null,
    markType: MarkType
): boolean {
    let changed = false;
    doc.nodesBetween(cellPos + 1, cellPos + cellNode.nodeSize - 1, (node, pos) => {
        if (!node.isText) return;
        const existing = node.marks.find((mark) => mark.type === markType);
        const raw = existing?.attrs.color;
        const current = typeof raw === 'string' ? normalizeHexColor(raw) : null;
        if (current === color) return;

        changed = true;
        const nextAttrs = { ...(existing?.attrs ?? {}), color };
        tr.removeMark(pos, pos + node.nodeSize, markType);
        const hasOther = Object.entries(nextAttrs).some(
            ([key, attrValue]) => key !== 'color' && attrValue != null && attrValue !== ''
        );
        if (color || hasOther) {
            tr.addMark(pos, pos + node.nodeSize, markType.create(nextAttrs));
        }
    });
    return changed;
}

export function createTableColorTransaction(
    state: EditorState,
    property: StyleColorProperty,
    value: string | null
): Transaction | null {
    const safeValue = value == null ? null : normalizeHexColor(value);
    if (value != null && !safeValue) return null;

    const cells = selectedTableCells(state);
    if (cells.length === 0) return null;

    const tr = state.tr;
    const markType = property === 'color' ? state.schema.marks.textStyle : undefined;
    let changed = false;

    cells.forEach(({ node, pos }) => {
        const currentStyle = cellStyle(node);
        const nextStyle = setStyleColor(currentStyle, property, safeValue);
        if (nextStyle !== currentStyle) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, style: nextStyle });
            changed = true;
        }
        if (markType && paintCellTextColor(tr, state.doc, pos, node, safeValue, markType)) {
            changed = true;
        }
    });

    return changed ? tr : null;
}

export function applyTableCellColor(
    editor: Editor,
    property: StyleColorProperty,
    value: string | null
): boolean {
    const safeValue = value == null ? null : normalizeHexColor(value);
    if (value != null && !safeValue) return false;
    if (selectedTableCells(editor.state).length === 0) return false;

    const tr = createTableColorTransaction(editor.state, property, safeValue);
    if (tr) editor.view.dispatch(tr);
    if (!editor.isDestroyed) editor.view.focus();
    return true;
}
